<script lang="ts">
import { defineComponent, h, withDirectives, vShow, computed, shallowRef, nextTick, unref } from 'vue';

import { classNames, convertToUnit } from '@components/utils';

import {
  CdsTransition,
  makeDimensionProps,
  makeDisabledProps,
  makeGroupItemProps,
  makeLazyProps,
  useDimensions,
  useDisabled,
  useGroupItem,
  useLazy,
  useRender,
  useSsrBoot
} from '../../composable';

import { CdsContainerGroupSymbol, useInjectContainer } from './context';

import type { PropType } from 'vue';

export const componentName = 'CdsContainerItem';
export default defineComponent({
  name: componentName,
  props: {
    reverseTransition: {
      type: [Boolean, String] as PropType<boolean | string>,
      default: undefined
    },
    transition: {
      type: [Boolean, String] as PropType<boolean | string>,
      default: undefined
    },

    ...makeDimensionProps(),
    ...makeGroupItemProps(),
    ...makeLazyProps(),
    ...makeDisabledProps()
  },
  emits: {
    'group:selected': (value: { value: boolean }) => true
  },
  setup(props, { slots }) {
    const container = useInjectContainer();
    const groupItem = useGroupItem(props, CdsContainerGroupSymbol);
    const { disabledClasses } = useDisabled(props);
    const { isBooted } = useSsrBoot();
    const { dimensionsStyles } = useDimensions(props);

    if (!container || !groupItem) {
      throw new Error('CdsContainerItem must be used inside a CdsContainer');
    }

    const isSelectable = computed(() => !unref(container.onlySingle) && !props.disabled && !!unref(groupItem.value));
    const isMultiple = computed(() => unref(container.multiple));

    const isTransitioning = shallowRef(false);
    const hasTransition = computed(
      () =>
        unref(isBooted) &&
        (unref(container.isReversed) ? props.reverseTransition !== false : props.transition !== false)
    );

    function onAfterTransition() {
      if (!unref(isTransitioning) || !container) return;

      isTransitioning.value = false;
      if (container.transitionCount.value > 0) {
        container.transitionCount.value -= 1;

        if (container.transitionCount.value === 0) {
          container.transitionHeight.value = undefined;
        }
      }
    }

    function onBeforeTransition() {
      if (unref(isTransitioning) || !container) return;

      isTransitioning.value = true;
      if (container.transitionCount.value === 0) {
        container.transitionHeight.value = convertToUnit(unref(container.rootRef)?.clientHeight);
      }

      container.transitionCount.value += 1;
    }

    function onTransitionCancelled() {
      onAfterTransition();
    }

    function onEnterTransition(el: Element) {
      if (!unref(isTransitioning)) return;

      nextTick(() => {
        if (!unref(hasTransition) || !unref(isTransitioning) || !container) return;

        container.transitionHeight.value = convertToUnit(el.clientHeight);
      });
    }

    const transition = computed(() => {
      const name = unref(container.isReversed) ? props.reverseTransition : props.transition;

      return !unref(hasTransition)
        ? false
        : {
            name: typeof name !== 'string' ? unref(container.transition) : name,
            onBeforeEnter: onBeforeTransition,
            onAfterEnter: onAfterTransition,
            onEnterCancelled: onTransitionCancelled,
            onBeforeLeave: onBeforeTransition,
            onAfterLeave: onAfterTransition,
            onLeaveCancelled: onTransitionCancelled,
            onEnter: onEnterTransition
          };
    });

    const { hasContent } = useLazy(props, groupItem.isSelected);

    useRender(() =>
      h(
        CdsTransition,
        {
          transition: unref(transition),
          disabled: !unref(isBooted)
        },
        {
          default: () =>
            withDirectives(
              h(
                'div',
                {
                  tabindex: unref(isSelectable) ? 0 : undefined,
                  class: classNames('cds-container__item', unref(disabledClasses), unref(groupItem.selectedClass), {
                    'cds-container__item--selectable': unref(isSelectable),
                    'cds-container__item--multiple': unref(isMultiple),
                    'cds-container__item--selected': unref(groupItem.isSelected)
                  }),
                  style: unref(dimensionsStyles),
                  onClick: () => !unref(container.onlySingle) && groupItem.select(!unref(groupItem.isSelected))
                },
                [
                  unref(isSelectable) &&
                    h(
                      'div',
                      {
                        class: 'cds-container__item-icon'
                      },
                      h('span', { class: 'cds-container__item-icon-after' })
                    ),
                  unref(container.onlySingle) ? unref(hasContent) && slots.default?.() : slots.default?.()
                ]
              ),
              [[vShow, !unref(container.onlySingle) || unref(groupItem.isSelected)]]
            )
        }
      )
    );

    return {};
  }
});
</script>
